/**
 * AssetSidePanel (UI-SPEC W5) — slide-in panel for asset detail / edit /
 * pick-on-map / soft-delete confirm. 480px overlay from end-side.
 *
 * Implementation lands in Plan 03-04 Task 03; this file is created in Task 02
 * as the type-stub MapView consumes. Read-mode anatomy + close button live
 * here from the start so MapView's compile + the W5 read-snapshot pass
 * before the edit/pick/delete flows land.
 *
 * Phase 3 ships this with the read-mode body fully wired; the edit form
 * (W9) + soft-delete modal (W16) become substantive in Task 03.
 */
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { X, Pencil, Trash2, Download } from "lucide-react";
import type { AssetFeature } from "../../api/assets";
import type { InspectionRead } from "../../api/inspections";
import { TypeChip } from "./TypeChip";
import { DimensionChip } from "./DimensionChip";
import { Button } from "../../components/Button";
import { Banner } from "../../components/Banner";
import { AssetEditForm } from "./AssetEditForm";
import { SoftDeleteConfirmModal } from "./SoftDeleteConfirmModal";
import { useAssetInspections } from "../inspection/useAssetInspections";
import { useClearPipeBlocked } from "../inspection/useClearPipeBlocked";
import { InspectionEditModal } from "../inspection/InspectionEditModal";
// Phase 7 Plan 03 (Critical Finding #5) — per-asset history PDF export button.
import { useExportPerAssetHistoryPdf } from "../../hooks/useExportPerAssetHistoryPdf";
// Phase 6 Plan 02 — replaces the flat-list inspection region with the
// structured HistorySection (D-132 + HIST-5.x). Plan 04-04 Edit-button
// pattern preserved end-to-end (HistorySection passes setEditingInspection
// through to InspectionRow's expanded Edit button).
import { HistorySection } from "../inspection/history/HistorySection";

interface AssetSidePanelProps {
  feature: AssetFeature;
  onClose: () => void;
  pickMode: boolean;
  onEnterPickMode: () => void;
  onExitPickMode: () => void;
  pickedCoords: { lat: number; lng: number } | null;
  onClearPickedCoords: () => void;
}

type PanelMode = "read" | "edit";

export function AssetSidePanel({
  feature,
  onClose,
  pickMode,
  onEnterPickMode,
  onExitPickMode,
  pickedCoords,
  onClearPickedCoords,
}: AssetSidePanelProps) {
  const intl = useIntl();
  const [mode, setMode] = useState<PanelMode>("read");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingInspection, setEditingInspection] =
    useState<InspectionRead | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Phase 11 D-19 — soft top-shadow on the sticky action footer when the body
  // has scrolled. RESEARCH § 6.1 lines 583-609. The shadow class is applied
  // conditionally on `scrolled`; `bodyRef` attaches the scroll listener to
  // the panel-body scrollable container.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 0);
    // Sync once on mount in case the body was already scrolled (e.g., panel
    // re-mounts at scrollTop > 0).
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
    // Re-attach when the read/edit mode flips because the scrollable body
    // unmounts/remounts and `bodyRef.current` changes.
  }, [mode]);
  const footerShadowClass = scrolled
    ? "shadow-[0_-2px_8px_rgba(0,0,0,0.06)]"
    : "";

  // Plan 04-04: per-asset inspection list (Plan 01 GET endpoint, B1 fix).
  const inspectionsQuery = useAssetInspections(feature.properties.id);
  const clearPipeBlocked = useClearPipeBlocked();
  // Phase 7 Plan 03 (Critical Finding #5) — actions footer EXPORT-10.5 button.
  const exportPdf = useExportPerAssetHistoryPdf();

  // Close on Esc.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (pickMode) {
          onExitPickMode();
          return;
        }
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
          return;
        }
        if (mode === "edit") {
          setMode("read");
          return;
        }
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, pickMode, showDeleteConfirm, onExitPickMode, onClose]);

  // Initial focus on close button.
  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  // When the underlying selected asset changes, re-render but DON'T close
  // (UI-SPEC W1: pin-tap-while-panel-open swaps content without flicker).
  // We also reset transient states (edit / delete-confirm / inspection-edit).
  useEffect(() => {
    setMode("read");
    setShowDeleteConfirm(false);
    setEditingInspection(null);
  }, [feature.properties.id]);

  const props = feature.properties;
  const lat = feature.geometry.coordinates[1];
  const lng = feature.geometry.coordinates[0];
  const isSoftDeleted =
    (props as unknown as { soft_deleted?: boolean }).soft_deleted === true;
  // Plan 04-04: derive `pipe_blocked` from the dimension color. Server
  // GeoJsonProperties (D-72) does NOT carry a raw boolean — only the
  // 4-color palette per Phase 3 derive_pipe_blocked. The only color the
  // dimension ever emits for a blocked asset is "red" (no yellow / no
  // hysteresis); a green/grey value means cleared. This keeps Plan 04
  // server-side untouched per the wave-2 scope contract.
  const isPipeBlocked = props.pipe_blocked_color === "red";

  // Pick-mode collapsed strip variant.
  if (pickMode) {
    return (
      <aside
        role="complementary"
        aria-label={intl.formatMessage({ id: "LABEL_PICK_ON_MAP" })}
        data-testid="asset-side-panel-strip"
        className="fixed top-0 bottom-0 end-0 w-16 bg-surface-raised shadow-lg ring-1 ring-divider flex flex-col items-center pbs-lg gap-sm z-30"
      >
        <button
          type="button"
          onClick={onExitPickMode}
          aria-label={intl.formatMessage({ id: "LABEL_CANCEL" })}
          data-testid="pick-cancel-button"
          className="h-9 w-9 rounded-md focus-visible:ring-2 focus-visible:ring-primary inline-flex items-center justify-center hover:bg-surface transition-colors duration-200"
        >
          <X size={18} aria-hidden="true" />
        </button>
        <p className="text-xs text-text-secondary text-center px-1">
          <FormattedMessage id="LABEL_PICK_ON_MAP_HINT" />
        </p>
      </aside>
    );
  }

  return (
    <>
      <aside
        ref={panelRef}
        role="complementary"
        aria-label={intl.formatMessage({ id: "LABEL_ASSET_NUMBER" })}
        data-testid="asset-side-panel"
        data-density="generous"
        // R6-007 (audit 2026-07): top-16, not top-0 — the panel and the
        // HomeShell header both resolve at z-30 in the root stacking context
        // and the panel is later in DOM order, so a full-height panel painted
        // over the header's entire control cluster (globe / Account /
        // Sign-out), making them unclickable while an asset was open.
        // Offsetting below the 64 px header band removes the overlap in both
        // LTR and RTL (both elements use end-0, so they flip together).
        className="fixed top-16 bottom-0 end-0 w-[480px] bg-surface-raised shadow-lg ring-1 ring-divider flex flex-col z-30 transition-transform duration-200 ease-out"
      >
        {/*
         * Header band (THEME-2.4) — inline gradient per UI-SPEC § Color §
         * Soft surface gradients. The gradient anchors at #406020 (primary)
         * and ramps to #5C7A3A; Tailwind v4's `bg-gradient-to-br from-primary
         * to-...` cannot reach the second stop without a derived token, so
         * inline style is the pragmatic choice.
         *
         * Phase 11 D-01 + D-26 (composition order: blur layer BENEATH gradient).
         * `backdrop-blur-sm [backdrop-filter:blur(8px)] [-webkit-backdrop-
         * filter:blur(8px)]` — Tailwind v4 backdrop-blur-sm = 8 px (verified
         * Context7 — RESEARCH § 1.5 + Pitfall 3); arbitrary-value escapes are
         * defensive (pin radius + emit Safari prefix). The gradient stops are
         * converted from solid hex to rgba(..., 0.96) so the blur of the map
         * cartography behind the panel reads THROUGH the gradient — without
         * any alpha, the gradient would be fully opaque and the backdrop-
         * filter would be invisible.
         *
         * **Why alpha=0.96 (not the plan-default 0.85):** the plan's body
         * proposed 0.85 with the conditional escape "if post-blend contrast
         * falls below AA, scale alpha UP." Plan 11-05 Task 2 contrast probe
         * (`web/tests/visual/asset-side-panel-header-contrast.spec.ts`)
         * statically computed white-on-blended-stop contrast against the
         * worst-case parchment-cartography backdrop (`#F8F6F1` = `--color-
         * surface`):
         *   - alpha=0.85 → sage stop blends to rgb(115,141,85), white
         *     contrast=3.70:1 → FAILS WCAG-AA 4.5:1 small-text floor.
         *   - alpha=0.96 → sage stop blends to rgb(98,127,65), white
         *     contrast=4.53:1 → PASSES.
         *   - alpha=1.00 (Phase 9 baseline) → sage stop = rgb(92,122,58),
         *     white contrast=4.88:1.
         * The 0.04 transparency vs Phase 9 baseline is enough for the blur
         * of the map cartography to read through softly while preserving the
         * accessibility floor. This is a Rule 1 auto-fix during Plan 11-05
         * Task 2 implementation; documented in the plan SUMMARY.
         *
         * Cross-browser fallback (D-23): `@supports not ((backdrop-filter:
         * blur(8px)) or (-webkit-backdrop-filter: blur(8px)))` block in
         * `web/src/styles/glassmorphism-fallback.css` zeros the backdrop-
         * filter on `[data-testid="asset-side-panel-header"]` for browsers
         * without support; the gradient (still rgba(..., 0.96)) reads as the
         * D-03 baseline. The 4 % alpha drop vs Phase 9 hex (0.96 vs 1.0) is
         * visually indistinguishable over the parchment surface underlying
         * the panel and stays above WCAG-AA.
         *
         * Composition: per CSS spec, `backdrop-filter` operates on the area
         * BEHIND the element; the element's own background-image (the gradient)
         * paints on TOP of the blurred backdrop. With 0.85-alpha gradient
         * stops, the gradient acts as a "brand wash" with the softened map
         * cartography bleeding through — the watercolor effect.
         *
         * RESEARCH Pitfall 7 audit (overflow:hidden ancestor breaking
         * compositing): VERIFIED at Plan 11-05 Task 2 implementation time.
         * The `<aside>` parent uses `transition-transform` for slide-in
         * (NOT `overflow:hidden`); HomeShell's main grid `min-h-0` does not
         * clip; no overflow-hidden ancestor exists in the chain. Backdrop-
         * filter compositing path is clean.
         */}
        <div
          data-testid="asset-side-panel-header"
          className="pbs-lg pbe-md ps-lg pe-lg border-be border-divider flex items-start gap-sm backdrop-blur-sm [backdrop-filter:blur(8px)] [-webkit-backdrop-filter:blur(8px)]"
          style={{
            background:
              "linear-gradient(135deg, rgba(64,96,32,0.96) 0%, rgba(92,122,58,0.96) 100%)",
          }}
        >
          <div className="flex-1 flex flex-col gap-xs">
            <div className="flex items-center gap-xs flex-wrap">
              <h2 className="text-2xl font-semibold text-on-primary">
                <bdi>{props.number}</bdi>
              </h2>
              <TypeChip type={props.type} />
              {isSoftDeleted && (
                <span
                  data-testid="soft-deleted-badge"
                  className="inline-flex items-center ps-xs pe-xs h-6 rounded-full text-sm font-semibold bg-error-container text-error ring-1 ring-error/30"
                >
                  <FormattedMessage id="LABEL_SOFT_DELETED_BADGE" />
                </span>
              )}
            </div>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label={intl.formatMessage({ id: "LABEL_CLOSE_PANEL" })}
            data-testid="panel-close-button"
            className="h-9 w-9 rounded-md focus-visible:ring-2 focus-visible:ring-primary inline-flex items-center justify-center hover:bg-surface text-on-primary transition-colors duration-200"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {/* Body — scrollable. Phase 11 D-19 attaches scroll listener so the
            sticky action footer below can show a soft top-shadow when content
            scrolls beneath. */}
        <div
          ref={bodyRef}
          className="flex-1 overflow-y-auto"
          data-testid="asset-side-panel-body"
        >
          {mode === "read" ? (
            <div data-testid="panel-identity-card">
              {/* Identity card */}
              <div className="pbs-md pbe-md ps-lg pe-lg">
                <Row label="LABEL_ASSET_NUMBER" value={props.number} />
                <Row
                  label="LABEL_TYPE"
                  value={intl.formatMessage({
                    id: props.type === "manhole" ? "LABEL_TYPE_MANHOLE" : "LABEL_TYPE_CATCH_BASIN",
                  })}
                />
                <Row label="LABEL_LATITUDE" value={lat.toFixed(6)} />
                <Row label="LABEL_LONGITUDE" value={lng.toFixed(6)} />
                {props.description ? (
                  <div className="pbs-xs">
                    <span className="text-sm font-semibold text-on-surface-variant">
                      <FormattedMessage id="LABEL_DESCRIPTION" />
                    </span>
                    <p className="text-base text-on-surface mbs-xxs">
                      <bdi>{props.description}</bdi>
                    </p>
                  </div>
                ) : null}
              </div>

              {/* Dimension chips */}
              <div className="pbs-md pbe-md ps-lg pe-lg border-bs border-divider">
                <h3 className="text-sm font-semibold text-on-surface-variant mbe-xs">
                  <FormattedMessage id="LABEL_CURRENT_STATE" />
                </h3>
                <div className="flex flex-wrap gap-xs">
                  <DimensionChip dimension="cleaning" color={props.cleaning_color} />
                  <DimensionChip dimension="coating" color={props.coating_color} />
                  <DimensionChip dimension="cover_level" color={props.cover_level_color} />
                  <DimensionChip dimension="pipe_blockage" color={props.pipe_blocked_color} />
                  <DimensionChip dimension="structure" color={props.structure_color} />
                </div>
              </div>

              {/* Inspections — Phase 6 Plan 02 D-132 HistorySection composition.
                  Replaces the Plan 04-04 flat list. Multiple-row simultaneous
                  expand, sort, AND-filter, audit trail + photo lifecycle live
                  here. The Edit button inside InspectionRow expanded state still
                  invokes setEditingInspection — same pattern as Plan 04-04. */}
              <div className="pbs-md pbe-md ps-lg pe-lg border-bs border-divider">
                <h3 className="text-2xl font-semibold text-on-surface mbs-lg mbe-md">
                  <FormattedMessage
                    id="LABEL_INSPECTIONS_COUNT"
                    values={{ count: inspectionsQuery.data?.length ?? 0 }}
                  />
                </h3>
                <HistorySection
                  assetId={feature.properties.id}
                  setEditingInspection={setEditingInspection}
                />
                {/* PM Clear-pipe-blocked button (CP2 closure) */}
                {isPipeBlocked && !isSoftDeleted && (
                  <div className="mbs-sm">
                    <Button
                      variant="destructive"
                      onClick={() =>
                        clearPipeBlocked.mutate({ assetId: props.id })
                      }
                      disabled={clearPipeBlocked.isPending}
                      data-testid="clear-pipe-blocked-btn"
                    >
                      <FormattedMessage id="LABEL_CLEAR_PIPE_BLOCKED" />
                    </Button>
                    {clearPipeBlocked.isError && (
                      <div className="mbs-xs">
                        <Banner
                          variant="error"
                          data-testid="clear-pipe-blocked-error"
                        >
                          <FormattedMessage
                            id={clearPipeBlocked.error?.code ?? "ERR_GENERIC"}
                          />
                        </Banner>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <AssetEditForm
              feature={feature}
              pickedCoords={pickedCoords}
              onClearPickedCoords={onClearPickedCoords}
              onEnterPickMode={onEnterPickMode}
              onCancel={() => setMode("read")}
              onSaved={() => setMode("read")}
            />
          )}
        </div>

        {/* Actions footer — only in read mode + only for non-deleted assets.
            Phase 11 D-19 — sticky-always-visible at panel bottom with soft
            top-shadow on scroll. The shadow class only renders when the
            scrollable body above has scrollTop > 0. RESEARCH § 6.1.
            D-21 lock — NO new sticky tabs added; this only wraps the existing
            v1.0 actions (Edit / Export PDF / Soft-delete) in a sticky
            container. The HistorySection accordion-row UX from HIST-5.1 is
            untouched. */}
        {mode === "read" && !isSoftDeleted && (
          <footer
            data-testid="asset-side-panel-footer"
            className={`sticky bottom-0 z-10 pbs-md pbe-lg ps-lg pe-lg border-bs border-divider bg-surface flex gap-md justify-end transition-shadow duration-200 ${footerShadowClass}`}
          >
            <Button
              variant="secondary"
              onClick={() => setMode("edit")}
              data-testid="panel-edit-button"
            >
              <span className="inline-flex items-center gap-xs">
                <Pencil size={16} aria-hidden="true" />
                <FormattedMessage id="LABEL_EDIT" />
              </span>
            </Button>
            {/* Phase 7 Plan 03 — EXPORT-10.5 per-asset history PDF (Critical Finding #5).
                Lives between Edit and Soft-delete in the actions footer; isPending
                disables the button while the Blob trigger is in-flight. */}
            <Button
              variant="secondary"
              onClick={() => exportPdf.mutate({ assetId: props.id })}
              disabled={exportPdf.isPending}
              data-testid="panel-export-history-pdf-button"
            >
              <span className="inline-flex items-center gap-xs">
                <Download size={16} aria-hidden="true" />
                <FormattedMessage id="LABEL_EXPORT_HISTORY_PDF_BUTTON" />
              </span>
            </Button>
            {/* Destructive button-label invariant (Phase 9 carry-forward) —
                LABEL_SOFT_DELETE is preserved verbatim; the verb-prefix is
                load-bearing. Phase 11 sticky-footer chrome MUST NOT regress
                this label to a generic "Delete" or "Remove". */}
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="panel-soft-delete-button"
            >
              <span className="inline-flex items-center gap-xs">
                <Trash2 size={16} aria-hidden="true" />
                <FormattedMessage id="LABEL_SOFT_DELETE" />
              </span>
            </Button>
          </footer>
        )}
      </aside>

      {showDeleteConfirm && (
        <SoftDeleteConfirmModal
          assetId={props.id}
          assetNumber={props.number}
          onClose={() => setShowDeleteConfirm(false)}
          onDeleted={() => {
            setShowDeleteConfirm(false);
            onClose();
          }}
        />
      )}

      {/* Plan 04-04: PM inspection-edit modal */}
      {editingInspection && (
        <InspectionEditModal
          inspection={editingInspection}
          onClose={() => setEditingInspection(null)}
        />
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-baseline pbs-xs pbe-xs">
      <span className="text-sm font-semibold text-on-surface-variant">
        <FormattedMessage id={label} />
      </span>
      <span className="text-base text-on-surface text-end">
        <bdi>{value}</bdi>
      </span>
    </div>
  );
}
